/**
 * @function getHostPermissions
 * @description Calcula y retorna todos los permisos del módulo Host (SSO y gestión de catálogos).
 * Esta función fábrica es invocada por `useRolePermissions` y recibe el estado de rol del usuario
 * junto con los helpers de verificación contra las listas del endpoint, garantizando que los
 * permisos reflejen tanto el rol jerárquico como los permisos dinámicos del backend.
 *
 * Incluye permisos para:
 * - **SSO — Corporativos**: visualización y creación/edición de usuarios corporativos.
 * - **SSO — Empresas**: visualización de empresas (solo rol Corporativo) y de la e.firma
 *   de la empresa propia (solo rol Empresa).
 * - **SSO — Usuarios**: visualización, creación y edición de usuarios regulares.
 * - **SSO — Roles**: visualización de roles y relación factura manual (solo rol Empresa).
 * - **Gestión — Tipo Documento**: CRUD completo de tipos de documento.
 * - **Gestión — Tipo Proveedor**: CRUD completo de tipos de proveedor.
 * - **Gestión — Relación Proveedor-Usuario**: CRUD completo de relaciones proveedor-usuario.
 *
 * @param {boolean} isSuper - Indica si el usuario tiene el rol "Super".
 * @param {boolean} isCorporativo - Indica si el usuario tiene el rol "Corporativo".
 * @param {boolean} isEmpresa - Indica si el usuario tiene el rol "Empresa".
 * @param {boolean} isUser - Indica si el usuario tiene el rol "Operativo".
 * @param {(roleCheck: boolean, sectionKey: string) => boolean} canViewByRoleOrSectionList
 *   Helper que verifica si el rol cumple la condición Y la sección está en la lista del endpoint.
 * @param {(roleCheck: boolean, actionKey: string) => boolean} canPerformByRoleOrActionList
 *   Helper que verifica si el rol cumple la condición Y la acción está en la lista del endpoint.
 * @returns Objeto plano con booleanos para cada permiso del módulo Host.
 */
export const getHostPermissions = (
  isSuper: boolean,
  isCorporativo: boolean,
  isEmpresa: boolean,
  isUser: boolean,
  canViewByRoleOrSectionList: (roleCheck: boolean, sectionKey: string) => boolean,
  canPerformByRoleOrActionList: (roleCheck: boolean, actionKey: string) => boolean
) => {
  return {
    // Permisos generales de gestión
    canViewManagementSection: isSuper || isCorporativo || isEmpresa,

    // Permisos SSO - Corporates
    canViewCorporates: isSuper,
    canViewCorporateButtons: isSuper,
    canCreateCorporateUsers: isSuper,
    canEditCorporateUsers: isSuper || isCorporativo,

    // Permisos SSO - Companies
    // `canViewCompanies` habilita el listado de empresas del corporativo, mientras que
    // `canViewCompanyEFirmas` habilita la e.firma de la propia empresa. Son permisos
    // independientes a propósito: unirlos expondría el listado completo al rol Empresa.
    canViewCompanies: isCorporativo,
    canViewCompanyEFirmas: isEmpresa,

    // Permisos SSO - Users
    canViewUsers: isSuper || isCorporativo || isEmpresa,
    canViewRegularUserButtons: isCorporativo || isEmpresa,
    canCreateRegularUsers: isCorporativo || isEmpresa,
    canEditRegularUsers: isCorporativo || isEmpresa,

    // Permisos SSO - Roles
    canViewRoles: isEmpresa,
    canViewRelacionFacturaManual: isEmpresa,
    // Conciliación SAT (PES-409): solo el administrador de la empresa (rol Empresa), igual que la API
    // (`ConciliacionController`). Que la empresa la tenga habilitada lo decide la API (403).
    canViewConciliacionSat: isEmpresa,
    // Solicitudes de reasignación (PES-759): las atiende el administrador de la empresa (rol Empresa).
    canViewSolicitudesReasignacion: isEmpresa,

    // Permisos de gestión - Tipo Documento
    canViewDocumentType: canViewByRoleOrSectionList(isUser, 'GestionarTipoDocumento'),
    canViewListDocumentType: canPerformByRoleOrActionList(isUser, 'VerTipoDocumento'),
    canCreateDocumentType: canPerformByRoleOrActionList(isUser, 'CrearTipoDocumento'),
    canEditDocumentType: canPerformByRoleOrActionList(isUser, 'EditarTipoDocumento'),
    canDeleteDocumentType: canPerformByRoleOrActionList(isUser, 'EliminarTipoDocumento'),

    // Permisos de gestión - Tipo Proveedor
    canViewProviderType: canViewByRoleOrSectionList(isUser, 'TipoProveedor'),
    canViewListProviderType: canPerformByRoleOrActionList(isUser, 'VerTipoProveedor'),
    canCreateProviderType: canPerformByRoleOrActionList(isUser, 'CrearTipoProveedor'),
    canEditProviderType: canPerformByRoleOrActionList(isUser, 'EditarTipoProveedor'),
    canDeleteProviderType: canPerformByRoleOrActionList(isUser, 'EliminarTipoProveedor'),

    // Permisos de gestión - Relación Proveedor Usuario
    canViewProviderUserRelation: canViewByRoleOrSectionList(isUser, 'RelacionUsuarioProveedor'),
    canViewListProviderUserRelation: canPerformByRoleOrActionList(isUser, 'VerRelacionProveedorUsuario'),
    canCreateProviderUserRelation: canPerformByRoleOrActionList(isUser, 'CrearRelacionProveedorUsuario'),
    canEditProviderUserRelation: canPerformByRoleOrActionList(isUser, 'EditarRelacionProveedorUsuario'),
    canDeleteProviderUserRelation: canPerformByRoleOrActionList(isUser, 'EliminarRelacionProveedorUsuario'),

    // Permisos de Auditoría — bitácora de cambios, solo lectura.
    // - Empresa lo ve por rol, sin depender de las listas del endpoint.
    // - Operativo se apoya en DOS listas distintas que emite el backend para los
    //   subperfiles "Operativo Auditor" y "Operativo Administrador": la sección
    //   `Bitacora` abre la pantalla y la actividad `VerBitacora` habilita el listado.
    // - La sección es la `Clave` de `dbo.CatalogoSeccion` que el SSO devuelve como
    //   `ClaveSeccion`; la migración V012 del SSO la alinea a `Bitacora`.
    // - No cruzar las listas: `canViewByRoleOrSectionList` solo busca en secciones y
    //   `canPerformByRoleOrActionList` solo en actividades. Cruzarlas compila y
    //   typechequea en verde, pero falla en runtime sin ningún error.
    canViewAuditoria: isEmpresa || canViewByRoleOrSectionList(isUser, 'Bitacora'),
    canViewListAuditoria: isEmpresa || canPerformByRoleOrActionList(isUser, 'VerBitacora'),

    // Buzón de notificaciones (M3-H1): el SSO solo entrega notificaciones a los roles
    // Operativo y Empresa. Se resuelve por rol, sin listas del endpoint: el buzón es
    // de cada usuario y no depende de una sección. A Super y Corporativo no se les
    // muestra la campanita ni la pantalla (el SSO les respondería ceros).
    canViewBuzon: isUser || isEmpresa,
  };
};

